import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';

const legendStyle = css`
	margin: 0;
	border: 0;
	padding: 0;
	min-width: 0;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem 1rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	line-height: 1.6;
	color: ${theme.colors.inkMuted};
`;

const swatchStyle = css`
	padding: 0 0.1875rem;
	color: ${theme.colors.ink};
`;

const tint = (color: string, percent: number): string =>
	`color-mix(in srgb, ${color} ${percent}%, transparent)`;

const toVerifyStyle = css`
	${swatchStyle}
	background-color: ${tint(theme.states.warning.ink, 20)};
	box-shadow: inset 0 -2px 0 ${theme.states.warning.ink};
`;

const unverifiedStyle = css`
	${swatchStyle}
	background-color: ${tint(theme.states.danger.ink, 14)};
	box-shadow: inset 0 -2px 0 ${theme.states.danger.ink};
`;

const verifiedStyle = css`
	color: ${theme.colors.ink};
	box-shadow: inset 0 -1px 0 ${tint(theme.states.success.ink, 60)};
`;

const notAddressedStyle = css`
	${swatchStyle}
	background-color: ${tint(theme.colors.inkFaint, 14)};
	outline: 1.5px dashed ${theme.colors.inkFaint};
`;

const invalidStyle = css`
	${swatchStyle}
	background-color: ${tint(theme.states.danger.solid, 12)};
	outline: 2px solid ${theme.states.danger.solid};
`;

const computedStyle = css`
	${swatchStyle}
	background-color: ${tint(theme.colors.inkFaint, 14)};
	box-shadow: inset 0 -1px 0 ${theme.colors.inkFaint};
`;

const conditionalStyle = css`
	${swatchStyle}
	background-image: linear-gradient(to right, ${theme.colors.accent} 50%, transparent 50%);
	background-size: 4px 2px;
	background-repeat: repeat-x;
	background-position: left bottom;
`;

/** Desktop legend of the viewer's markings; each sample follows the tone the viewer paints for that state. */
const MarkingLegend: React.FC = () => {
	return (
		<fieldset aria-label="Légende des marquages" css={legendStyle}>
			<span css={toVerifyStyle}>À vérifier</span>
			<span css={verifiedStyle}>Vérifiée ou corrigée</span>
			<span css={unverifiedStyle}>Non vérifiée</span>
			<span css={notAddressedStyle}>À compléter</span>
			<span css={invalidStyle}>Invalide</span>
			<span css={computedStyle}>Calculé</span>
			<span css={conditionalStyle}>Texte lié à une valeur</span>
		</fieldset>
	);
};

export default MarkingLegend;
